import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { translatePath, langFromPath } from "../lib/langRoutes";

const SITE_URL = "https://kosmedico.zuerich";

const LOCALE_MAP = { pt: "pt_PT", en: "en_GB", de: "de_CH" };

const upsertMeta = (key, attr, value) => {
  if (!value) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const upsertLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Set page title, description, canonical, hreflang, OG and Twitter cards.
 *
 * `canonicalPath` should be the language-agnostic in-app path (without
 * language prefix); the hook resolves the correct language prefix + prices
 * slug for the current locale, and emits alternates for the other locales
 * via `translatePath`.
 */
export const useSeo = ({ title, description, canonicalPath, image } = {}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = i18n.language?.split("-")[0] || langFromPath(pathname);

  useEffect(() => {
    if (title) document.title = title;
    document.documentElement.setAttribute("lang", lang);

    if (description) upsertMeta("description", "name", description);

    // Determine canonical URL. The provided canonicalPath is treated as the
    // DE (canonical) path (e.g. "/preise"). For the current language we run
    // it through translatePath so localized slugs (preise/precos/prices) are
    // swapped correctly instead of just prefixed literally.
    const currentPath = canonicalPath
      ? translatePath(canonicalPath, lang)
      : pathname || "/";
    const url = `${SITE_URL}${currentPath || "/"}`;

    upsertLink("canonical", url);

    // Hreflang alternates: point each language variant to its real URL.
    ["de", "pt", "en"].forEach((l) => {
      const altPath = translatePath(currentPath || "/", l);
      upsertLink("alternate", `${SITE_URL}${altPath}`, l);
    });
    // x-default → German (canonical)
    const defaultPath = translatePath(currentPath || "/", "de");
    upsertLink("alternate", `${SITE_URL}${defaultPath}`, "x-default");

    // Open Graph
    upsertMeta("og:type", "property", "website");
    upsertMeta("og:site_name", "property", "KosMedico Zürich");
    if (title) upsertMeta("og:title", "property", title);
    if (description) upsertMeta("og:description", "property", description);
    upsertMeta("og:url", "property", url);
    if (image) upsertMeta("og:image", "property", image);
    if (LOCALE_MAP[lang]) upsertMeta("og:locale", "property", LOCALE_MAP[lang]);

    // Twitter Card
    upsertMeta("twitter:card", "name", "summary_large_image");
    if (title) upsertMeta("twitter:title", "name", title);
    if (description) upsertMeta("twitter:description", "name", description);
    if (image) upsertMeta("twitter:image", "name", image);
  }, [title, description, canonicalPath, image, lang, pathname]);
};

export { SITE_URL };
